/**
 * Provider-neutral tool-call handling for the PowerPoint native agent.
 *
 * Every provider session hands its parsed tool calls here and gets back one
 * outcome per call; only the encoding of those outcomes onto the wire is
 * provider-specific. Argument validation, policy gates, execution, timeline
 * events, and golden traces therefore behave identically for GPT and Claude.
 */

import type {
  PresentationContent,
  PresentationModification,
  ReferenceSlideImage,
  ToolCall,
  ToolResult,
} from "../../../types";
import type { AgentToolContext, AiRequestOptions, ToolExecutionPolicy } from "../types";
import type { AgentEvent } from "../agentStream";
import { buildBlockedToolResult, executeTools, formatToolCallArgs, isToolAllowed } from "../tools";
import { sanitizeCompanyLogoToolData } from "../logoCompanyRefs";
import { parseDeckPlanFromToolArgs, parseSlideOutlinePatch } from "../deckPlanAgentTools";
import { uploadGoldenArtifactJson } from "../../goldenArtifactsService";
import { runGoldenCaptureStep } from "../../../utils/goldenCapture";

/** One executed tool call, ready for a provider session to encode. */
export interface NativeToolOutcome {
  callId: string;
  tool: string;
  /** The JSON payload returned to the model (`tool`, `success`, `data`, `error`). */
  output: Record<string, unknown>;
  isError: boolean;
  /** Screenshots captured by this call. */
  referenceImages?: ReferenceSlideImage[];
}

export interface NativeToolHandlingResult {
  outcomes: NativeToolOutcome[];
  toolResults: ToolResult[];
  referenceImages: ReferenceSlideImage[];
}

export const FUNCTION_CALL_NO_OUTPUT_ERROR = "Function did not return any output.";

export const NATIVE_AGENT_TOOL_NAMES = new Set<ToolCall["tool"]>([
  "get_company_logo",
  "finster_query",
  "factset_ai_query",
  "get_slide_images",
  "apply_edits",
  "get_slides_info",
  "insert_library_slide",
  "apply_preslink",
  "list_slide_library_collections",
  "search_slide_library",
  "get_library_slide_previews",
  "present_deck_plan",
  "update_deck_plan",
  "active_graph",
  "create_preslink_chart",
  "list_active_graph_types",
  "id_lookup",
]);

export interface NativeFunctionCall {
  callId: string;
  name: string;
  rawArguments: string;
}

export interface GoldenToolTraceEntry {
  sequence: number;
  loopIndex?: number;
  callIndexInLoop: number;
  callId: string;
  toolName: string;
  subdirectory?: string;
  status: "pending" | "success" | "error" | "blocked" | "parse_error";
  success?: boolean;
  error?: string;
  files: string[];
  requestedReturn?: { json?: boolean; image?: boolean };
  referenceImageCount?: number;
  hasSlidesInfo?: boolean;
  createdSlideCount?: number;
}

export interface NativeLoopCallbacks {
  onThinking?: (text: string) => void;
  onToolCall?: (tool: string, args: string) => void;
  onToolResult?: (results: ToolResult[]) => void;
  onAgentEvent?: (event: AgentEvent) => void;
}

/** Error results for calls that never produced output (loop boundary or replay gaps). */
export function buildFallbackResultsForUnansweredCalls(
  calls: NativeFunctionCall[],
  errorMessage: string
): ToolResult[] {
  return calls.map((call) => ({
    tool: call.name || "unknown_tool",
    success: false,
    error: errorMessage,
  }));
}

export function normalizeToolResultForOutput(result: ToolResult): Record<string, unknown> {
  let normalizedData = result.data;
  if (normalizedData && typeof normalizedData === "object") {
    if (result.tool === "get_company_logo") {
      normalizedData = sanitizeCompanyLogoToolData(normalizedData);
    } else if ("debugLog" in normalizedData) {
      const { debugLog: _debugLog, ...rest } = normalizedData as Record<string, unknown>;
      normalizedData = rest;
    }
  }
  const payload: Record<string, unknown> = {
    tool: result.tool,
    success: result.success,
  };
  if (normalizedData !== undefined) {
    payload.data = normalizedData;
  }
  if (result.error) {
    payload.error = result.error;
  }
  return payload;
}

function jsonSafeClone<T>(value: T): T {
  return JSON.parse(
    JSON.stringify(value, (_key, nestedValue) => {
      if (nestedValue instanceof Error) {
        return { name: nestedValue.name, message: nestedValue.message, stack: nestedValue.stack };
      }
      return nestedValue;
    })
  ) as T;
}

function buildReferenceImageArtifacts(
  images: ReferenceSlideImage[] | undefined
): Array<Record<string, unknown>> {
  return (images || []).map(({ slideId, image: base64 }, index) => ({
    index,
    slideId,
    base64,
    base64Length: base64.length,
    approxBytes: Math.ceil((base64.length * 3) / 4),
  }));
}

function buildRequestedReturn(
  call: ToolCall | undefined
): { json?: boolean; image?: boolean } | undefined {
  if (!call || (call.tool !== "apply_edits" && call.tool !== "get_slides_info")) return undefined;
  const requested: { json?: boolean; image?: boolean } = {};
  if (call.returnJson === true) requested.json = true;
  if (call.returnImage === true) requested.image = true;
  return Object.keys(requested).length > 0 ? requested : undefined;
}

function hasSlidesInfo(result: ToolResult | undefined): boolean | undefined {
  const data = result?.data;
  if (!data || typeof data !== "object") return undefined;
  return "slidesInfo" in data ? true : undefined;
}

function getCreatedSlideCount(result: ToolResult | undefined): number | undefined {
  const data = result?.data;
  if (!data || typeof data !== "object") return undefined;
  const createdSlides = (data as Record<string, unknown>).createdSlides;
  return Array.isArray(createdSlides) ? createdSlides.length : undefined;
}

function buildToolTraceFiles(toolName: string): string[] {
  const files = ["tool_call.json"];
  if (toolName === "apply_edits") {
    files.push("operations.json", "prepared_batch_operations.json");
  }
  files.push("tool_output.json");
  return files;
}

function resolveTraceStatus(input: {
  pending: {
    blockedReason?: string;
    parseError?: string;
  };
  toolResult?: ToolResult;
}): GoldenToolTraceEntry["status"] {
  if (input.pending.blockedReason) return "blocked";
  if (input.pending.parseError) return "parse_error";
  return input.toolResult?.success ? "success" : "error";
}

export async function captureGoldenToolTrace(
  ctx: AgentToolContext | undefined,
  entries: GoldenToolTraceEntry[]
): Promise<void> {
  if (!ctx?.goldenArtifactRun) return;
  const traceSubdirectory = ctx.goldenToolTraceSubdirectory;
  await runGoldenCaptureStep(
    ctx.goldenArtifactRun,
    ctx.log ?? (() => {}),
    "tool_trace.json",
    async () => {
      await uploadGoldenArtifactJson(
        ctx.goldenArtifactRun!.runId,
        "tool_trace",
        {
          runId: ctx.goldenArtifactRun!.runId,
          generatedAt: new Date().toISOString(),
          entries: jsonSafeClone(entries),
        },
        traceSubdirectory ? { subdirectory: traceSubdirectory } : undefined
      );
    }
  );
}

async function captureGoldenToolCall(input: {
  ctx?: AgentToolContext;
  subdirectory?: string;
  sequence?: number;
  loopIndex?: number;
  callIndexInLoop: number;
  callId: string;
  toolName: string;
  rawArguments?: string;
  parsedToolCall?: ToolCall;
  formattedArgs?: string;
  blockedReason?: string;
  parseError?: string;
}): Promise<void> {
  const { ctx, subdirectory } = input;
  if (!ctx?.goldenArtifactRun || !subdirectory) return;
  await runGoldenCaptureStep(
    ctx.goldenArtifactRun,
    ctx.log ?? (() => {}),
    `${subdirectory}/tool_call.json`,
    async () => {
      await uploadGoldenArtifactJson(
        ctx.goldenArtifactRun!.runId,
        "tool_call",
        jsonSafeClone({
          runId: ctx.goldenArtifactRun!.runId,
          generatedAt: new Date().toISOString(),
          sequence: input.sequence,
          loopIndex: input.loopIndex,
          callIndexInLoop: input.callIndexInLoop,
          callId: input.callId,
          toolName: input.toolName,
          rawArguments: input.rawArguments ?? "",
          parsedToolCall: input.parsedToolCall,
          formattedArgs: input.formattedArgs,
          blockedReason: input.blockedReason,
          parseError: input.parseError,
        }),
        { subdirectory }
      );
    }
  );
}

async function captureGoldenToolOutput(input: {
  ctx?: AgentToolContext;
  subdirectory?: string;
  sequence?: number;
  loopIndex?: number;
  callIndexInLoop: number;
  callId: string;
  toolName: string;
  toolResult: ToolResult;
  normalizedFunctionOutput: Record<string, unknown>;
  /** The provider's own result item, keyed by its artifact field name. */
  providerResultItem: Record<string, unknown>;
  referenceImages?: ReferenceSlideImage[];
}): Promise<void> {
  const { ctx, subdirectory } = input;
  if (!ctx?.goldenArtifactRun || !subdirectory) return;
  const capturedToolResult = input.toolResult;
  const capturedNormalizedOutput = input.normalizedFunctionOutput;
  await runGoldenCaptureStep(
    ctx.goldenArtifactRun,
    ctx.log ?? (() => {}),
    `${subdirectory}/tool_output.json`,
    async () => {
      await uploadGoldenArtifactJson(
        ctx.goldenArtifactRun!.runId,
        "tool_output",
        jsonSafeClone({
          runId: ctx.goldenArtifactRun!.runId,
          generatedAt: new Date().toISOString(),
          sequence: input.sequence,
          loopIndex: input.loopIndex,
          callIndexInLoop: input.callIndexInLoop,
          callId: input.callId,
          toolName: input.toolName,
          toolResult: capturedToolResult,
          normalizedFunctionOutput: capturedNormalizedOutput,
          ...input.providerResultItem,
          referenceImages: buildReferenceImageArtifacts(input.referenceImages),
        }),
        { subdirectory }
      );
    }
  );
}

function parseToolCallArguments(rawArguments: string): Record<string, unknown> {
  const parsed = JSON.parse(rawArguments);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Tool arguments must be a JSON object");
  }
  return parsed as Record<string, unknown>;
}

function requireNonEmptyStringArgument(args: Record<string, unknown>, key: string): string {
  const value = args[key];
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`"${key}" must be a non-empty string`);
  }
  return value.trim();
}

function requireNonEmptyStringArrayArgument(args: Record<string, unknown>, key: string): string[] {
  const value = args[key];
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`"${key}" must be a non-empty array of strings`);
  }
  return value.map((item) => {
    if (typeof item !== "string" || item.trim().length === 0) {
      throw new Error(`"${key}" must be a non-empty array of strings`);
    }
    return item.trim();
  });
}

function requirePositiveNumberArgument(
  args: Record<string, unknown>,
  key: string,
  options: { integer?: boolean } = {}
): number {
  const value = args[key];
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    throw new Error(`"${key}" must be a positive number`);
  }
  return options.integer ? Math.floor(value) : value;
}

function parseAndValidateNativeToolCall(
  toolName: ToolCall["tool"],
  parsedArgs: Record<string, unknown>
): ToolCall {
  switch (toolName) {
    case "finster_query":
    case "factset_ai_query": {
      const query = requireNonEmptyStringArgument(parsedArgs, "query");
      // queryMode is required by the schema; fall back to "thinking" (deeper)
      // for any missing/invalid value to preserve answer quality.
      const queryMode = parsedArgs.queryMode === "standard" ? "standard" : "thinking";
      return {
        tool: toolName,
        query,
        queryMode,
      };
    }
    case "get_company_logo": {
      const companyNames = requireNonEmptyStringArrayArgument(parsedArgs, "company_names");
      const toolCall: ToolCall = {
        tool: "get_company_logo",
        company_names: companyNames,
      };
      if (parsedArgs.size !== undefined) {
        toolCall.size = requirePositiveNumberArgument(parsedArgs, "size", { integer: true });
      }
      return toolCall;
    }
    case "active_graph": {
      const operation = requireNonEmptyStringArgument(parsedArgs, "operation");
      const allowedOperations = new Set([
        "create_active_graph",
        "inspect_selected_active_graph",
        "list_active_graph_stylesets",
        "update_active_graph_frame",
        "delete_selected_active_graph",
      ]);
      if (!allowedOperations.has(operation)) {
        throw new Error(`Unsupported active_graph operation "${operation}"`);
      }
      return {
        tool: "active_graph",
        activeGraphInput: {
          ...parsedArgs,
          operation,
        } as unknown as NonNullable<ToolCall["activeGraphInput"]>,
      };
    }
    case "create_preslink_chart": {
      if (
        !parsedArgs.chartParameters ||
        typeof parsedArgs.chartParameters !== "object" ||
        Array.isArray(parsedArgs.chartParameters)
      ) {
        throw new Error(`"chartParameters" must be a ChartCreator parameter object`);
      }
      if (
        parsedArgs.target !== undefined &&
        (!parsedArgs.target ||
          typeof parsedArgs.target !== "object" ||
          Array.isArray(parsedArgs.target))
      ) {
        throw new Error(`"target" must be an object when provided`);
      }
      return {
        tool: "create_preslink_chart",
        preslinkChartInput: parsedArgs as unknown as NonNullable<ToolCall["preslinkChartInput"]>,
      };
    }
    case "list_active_graph_types":
      return { tool: "list_active_graph_types" };
    case "id_lookup":
      return {
        tool: "id_lookup",
        idLookupSearchText: requireNonEmptyStringArgument(parsedArgs, "search_text"),
      };
    case "get_slide_images": {
      const slideNumbersRaw = parsedArgs.slide_numbers;
      if (!Array.isArray(slideNumbersRaw) || slideNumbersRaw.length === 0) {
        throw new Error(`"slide_numbers" must be a non-empty array of non-negative numbers`);
      }
      const slideNumbers = slideNumbersRaw.map((value) => {
        if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
          throw new Error(`"slide_numbers" must be a non-empty array of non-negative numbers`);
        }
        return Math.floor(value);
      });
      const toolCall: ToolCall = {
        tool: "get_slide_images",
        slide_numbers: slideNumbers,
      };
      if (parsedArgs.max_images !== undefined) {
        toolCall.max_images = requirePositiveNumberArgument(parsedArgs, "max_images", {
          integer: true,
        });
      }
      return toolCall;
    }
    case "apply_edits": {
      const mods = parsedArgs.modifications;
      if (!mods || typeof mods !== "object" || Array.isArray(mods)) {
        throw new Error(`"modifications" must be a PresentationModification object`);
      }
      const toolCall: ToolCall = {
        tool: "apply_edits",
        modifications: mods as unknown as PresentationModification,
      };
      const ret = parsedArgs.return;
      if (ret && typeof ret === "object" && !Array.isArray(ret)) {
        const flags = ret as Record<string, unknown>;
        if (flags.json === true) toolCall.returnJson = true;
        if (flags.image === true) toolCall.returnImage = true;
      }
      return toolCall;
    }
    case "get_slides_info": {
      const slideNumbersRaw = parsedArgs.slide_numbers;
      const wantsLayoutMetadata =
        parsedArgs.layouts === true ||
        parsedArgs.designs === true ||
        parsedArgs.master_shapes === true ||
        Array.isArray(parsedArgs.layout_names);
      if (
        (!Array.isArray(slideNumbersRaw) || slideNumbersRaw.length === 0) &&
        !wantsLayoutMetadata
      ) {
        throw new Error(
          `"slide_numbers" must be a non-empty array of non-negative numbers unless requesting layout/design metadata`
        );
      }
      const slideNumbers = Array.isArray(slideNumbersRaw)
        ? slideNumbersRaw.map((value) => {
            if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
              throw new Error(`"slide_numbers" must contain only non-negative numbers`);
            }
            return Math.floor(value);
          })
        : [];
      const toolCall: ToolCall = { tool: "get_slides_info", slide_numbers: slideNumbers };
      if (parsedArgs.json === true) toolCall.returnJson = true;
      if (parsedArgs.image === true) toolCall.returnImage = true;
      if (parsedArgs.layouts === true) toolCall.returnLayouts = true;
      if (Array.isArray(parsedArgs.layout_names)) {
        toolCall.layout_names = parsedArgs.layout_names.filter(
          (value): value is string => typeof value === "string" && value.trim().length > 0
        );
      }
      if (parsedArgs.designs === true) toolCall.returnDesigns = true;
      if (parsedArgs.master_shapes === true) toolCall.returnMasterShapes = true;
      return toolCall;
    }
    case "insert_library_slide": {
      const toolCall: ToolCall = {
        tool: "insert_library_slide",
        librarySlideId: requireNonEmptyStringArgument(parsedArgs, "library_slide_id"),
      };
      const afterSlideId = parsedArgs.after_slide_id;
      if (typeof afterSlideId === "string" && afterSlideId.trim().length > 0) {
        toolCall.afterSlideId = afterSlideId.trim();
      }
      return toolCall;
    }
    case "apply_preslink": {
      const operation =
        parsedArgs.operation === "refresh" || parsedArgs.operation === "inspect"
          ? parsedArgs.operation
          : parsedArgs.process_presentation_file === true
            ? "refresh"
            : "inspect";
      const shapesRaw = parsedArgs.shapes;
      if (!Array.isArray(shapesRaw) || shapesRaw.length === 0) {
        throw new Error(`"shapes" must be a non-empty array of { slide_id, shape_id } objects`);
      }
      const preslinkShapeRefs = shapesRaw.map((entry) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
          throw new Error(`each "shapes" entry must be an object with slide_id and shape_id`);
        }
        const record = entry as Record<string, unknown>;
        const slideId = typeof record.slide_id === "string" ? record.slide_id.trim() : "";
        const id = typeof record.shape_id === "string" ? record.shape_id.trim() : "";
        if (!slideId || !id) {
          throw new Error(`each "shapes" entry requires non-empty slide_id and shape_id strings`);
        }
        return { slideId, id };
      });
      const toolCall: ToolCall = {
        tool: "apply_preslink",
        preslinkOperation: operation,
        preslinkShapeRefs,
      };
      if (parsedArgs.include_ppt_file === true) toolCall.includePptFile = true;
      if (parsedArgs.include_cell_data === false) toolCall.includeCellData = false;
      if (operation === "refresh") toolCall.processPresentationFile = true;
      if (parsedArgs.scope === "deck" || parsedArgs.scope === "shapes") {
        toolCall.preslinkScope = parsedArgs.scope;
      }
      return toolCall;
    }
    case "list_slide_library_collections":
      return { tool: "list_slide_library_collections" };
    case "search_slide_library": {
      const toolCall: ToolCall = { tool: "search_slide_library" };
      const collectionIds: string[] = [];
      if (Array.isArray(parsedArgs.collection_ids)) {
        for (const value of parsedArgs.collection_ids) {
          if (typeof value === "string" && value.trim()) {
            collectionIds.push(value.trim());
          }
        }
      }
      const collectionId = parsedArgs.collection_id;
      if (typeof collectionId === "string" && collectionId.trim()) {
        collectionIds.push(collectionId.trim());
      }
      if (collectionIds.length > 0) {
        toolCall.collectionIds = [...new Set(collectionIds)];
      }
      if (parsedArgs.scope === "all") {
        toolCall.slideLibraryScope = "all";
      }
      if (Array.isArray(parsedArgs.tags)) {
        toolCall.tags = parsedArgs.tags.filter(
          (value): value is string => typeof value === "string" && value.trim().length > 0
        );
      }
      if (typeof parsedArgs.offset === "number" && Number.isFinite(parsedArgs.offset)) {
        toolCall.offset = Math.max(0, Math.floor(parsedArgs.offset));
      }
      if (typeof parsedArgs.limit === "number" && Number.isFinite(parsedArgs.limit)) {
        toolCall.limit = Math.min(200, Math.max(1, Math.floor(parsedArgs.limit)));
      }
      return toolCall;
    }
    case "get_library_slide_previews":
      return {
        tool: "get_library_slide_previews",
        slideIds: requireNonEmptyStringArrayArgument(parsedArgs, "slide_ids").slice(0, 200),
      };
    case "present_deck_plan":
      return {
        tool: "present_deck_plan",
        deckPlan: parseDeckPlanFromToolArgs(parsedArgs),
      };
    case "update_deck_plan": {
      const toolCall: ToolCall = { tool: "update_deck_plan" };
      if (Array.isArray(parsedArgs.slides) && parsedArgs.slides.length > 0) {
        toolCall.deckPlan = parseDeckPlanFromToolArgs(parsedArgs, "Current plan");
      } else if (typeof parsedArgs.topic === "string" && parsedArgs.topic.trim()) {
        toolCall.deckPlan = {
          topic: parsedArgs.topic.trim(),
          slideCount: 0,
          slides: [],
        };
      }
      if (Array.isArray(parsedArgs.patches)) {
        toolCall.deckPlanPatches = parsedArgs.patches
          .map((patch, index) => parseSlideOutlinePatch(patch, index))
          .filter((slide): slide is NonNullable<typeof slide> => Boolean(slide));
      }
      if (!toolCall.deckPlan && !toolCall.deckPlanPatches?.length) {
        throw new Error(`update_deck_plan requires "slides" or "patches"`);
      }
      return toolCall;
    }
    default:
      return {
        tool: toolName,
      };
  }
}

/**
 * Validates, policy-checks, and executes one hop's tool calls, emitting the
 * timeline events and golden traces both providers share. Returns one outcome
 * per call, in call order, for the provider session to encode.
 */
export async function handleNativeFunctionCalls(
  calls: NativeFunctionCall[],
  presentationContent: PresentationContent,
  callbacks: NativeLoopCallbacks,
  toolPolicy: ToolExecutionPolicy | undefined,
  agentToolContext: AgentToolContext | undefined,
  loopIndex: number | undefined,
  goldenToolTraceEntries: GoldenToolTraceEntry[],
  langfuseContext: AiRequestOptions["langfuseContext"] | undefined,
  toProviderResultItem: (outcome: NativeToolOutcome) => Record<string, unknown>
): Promise<NativeToolHandlingResult> {
  type PendingNativeCall = {
    sequence: number;
    callId: string;
    tool: string;
    callIndexInLoop: number;
    rawArguments: string;
    goldenSubdirectory?: string;
    parsedToolCall?: ToolCall;
    formattedArgs?: string;
    parseError?: string;
    blockedReason?: string;
    referenceImages?: ReferenceSlideImage[];
  };

  const pendingCalls: PendingNativeCall[] = [];
  const executableToolCalls: ToolCall[] = [];
  const executableGoldenSubdirectories: Array<string | undefined> = [];
  const executableCallIds: string[] = [];

  for (let callIndex = 0; callIndex < calls.length; callIndex += 1) {
    const call = calls[callIndex];
    const goldenSubdirectory =
      agentToolContext?.goldenArtifactRun && agentToolContext.nextGoldenToolSubdirectory
        ? agentToolContext.nextGoldenToolSubdirectory(call.name)
        : undefined;
    const sequence = goldenToolTraceEntries.length;
    goldenToolTraceEntries.push({
      sequence,
      loopIndex,
      callIndexInLoop: callIndex,
      callId: call.callId,
      toolName: call.name,
      subdirectory: goldenSubdirectory,
      status: "pending",
      files: ["tool_call.json"],
    });
    const pending: PendingNativeCall = {
      sequence,
      callId: call.callId,
      tool: call.name,
      callIndexInLoop: callIndex,
      rawArguments: call.rawArguments,
      goldenSubdirectory,
    };

    if (!NATIVE_AGENT_TOOL_NAMES.has(call.name as ToolCall["tool"])) {
      pending.parseError = `Unsupported tool name: ${call.name}`;
      pendingCalls.push(pending);
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        parseError: pending.parseError,
      });
      continue;
    }

    if (!isToolAllowed(call.name, toolPolicy)) {
      pending.blockedReason = `Tool ${call.name} is disabled for this request context.`;
      pendingCalls.push(pending);
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        blockedReason: pending.blockedReason,
      });
      continue;
    }

    try {
      const parsedArgs = parseToolCallArguments(call.rawArguments);
      const parsedToolCall = parseAndValidateNativeToolCall(
        call.name as ToolCall["tool"],
        parsedArgs
      );
      pending.parsedToolCall = parsedToolCall;
      pending.formattedArgs = formatToolCallArgs(parsedToolCall);
      pendingCalls.push(pending);
      executableToolCalls.push(parsedToolCall);
      executableGoldenSubdirectories.push(goldenSubdirectory);
      executableCallIds.push(call.callId);
      callbacks.onToolCall?.(parsedToolCall.tool, pending.formattedArgs);
      callbacks.onAgentEvent?.({
        kind: "tool_call",
        hop: loopIndex ?? 0,
        callId: call.callId,
        tool: parsedToolCall.tool,
        argsPreview: pending.formattedArgs,
        status: "calling",
      });
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        parsedToolCall,
        formattedArgs: pending.formattedArgs,
      });
    } catch (error) {
      pending.parseError = `Malformed tool arguments: ${error instanceof Error ? error.message : String(error)}`;
      pendingCalls.push(pending);
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        parseError: pending.parseError,
      });
    }
  }

  const executed =
    executableToolCalls.length > 0
      ? await executeTools(executableToolCalls, {
          presentationContent,
          finsterDataSources: toolPolicy?.finsterDataSources,
          toolPolicy,
          agentToolContext,
          langfuseContext,
          onToolProgress: callbacks.onAgentEvent
            ? (toolCallIndex, message) => {
                const callId = executableCallIds[toolCallIndex];
                const tool = executableToolCalls[toolCallIndex]?.tool;
                if (!callId || !tool) return;
                callbacks.onAgentEvent?.({
                  kind: "tool_progress",
                  hop: loopIndex ?? 0,
                  callId,
                  tool,
                  text: message,
                });
              }
            : undefined,
          onToolEvent: callbacks.onAgentEvent
            ? (toolCallIndex, event) => {
                const onAgentEvent = callbacks.onAgentEvent;
                if (!onAgentEvent) return;
                const callId = executableCallIds[toolCallIndex];
                if (!callId) return;
                const hop = loopIndex ?? 0;
                switch (event.type) {
                  case "reasoning":
                    onAgentEvent({
                      kind: "subagent_reasoning_delta",
                      hop,
                      callId,
                      subHop: event.hopIndex,
                      text: event.chunk,
                    });
                    break;
                  case "tool":
                    onAgentEvent({
                      kind: "subagent_tool_call",
                      hop,
                      callId,
                      subHop: event.hopIndex,
                      subCallId: event.callId || `${event.toolName}-${event.hopIndex}`,
                      tool: event.toolName,
                      status: event.status,
                      description: event.description,
                      sectionTitle: event.sectionTitle,
                    });
                    break;
                  case "answerDelta":
                    onAgentEvent({ kind: "subagent_output_delta", hop, callId, text: event.chunk });
                    break;
                  case "final":
                    onAgentEvent({
                      kind: "subagent_result",
                      hop,
                      callId,
                      answer: event.answer,
                      citations: event.citations,
                    });
                    break;
                  case "status":
                  case "session":
                    break;
                }
              }
            : undefined,
          ...(executableGoldenSubdirectories.some(Boolean)
            ? { goldenSubdirectories: executableGoldenSubdirectories }
            : {}),
        })
      : {
          results: [] as ToolResult[],
          referenceImages: [] as ReferenceSlideImage[],
          referenceImagesByResultIndex: [] as Array<ReferenceSlideImage[] | undefined>,
        };

  const toolResults: ToolResult[] = [];
  let executedIndex = 0;
  for (const pending of pendingCalls) {
    if (pending.blockedReason) {
      toolResults.push(buildBlockedToolResult(pending.tool, pending.blockedReason));
      continue;
    }
    if (pending.parseError) {
      toolResults.push({
        tool: pending.tool,
        success: false,
        error: pending.parseError,
      });
      continue;
    }
    const executedResult = executed.results[executedIndex];
    if (!executedResult) {
      toolResults.push({
        tool: pending.tool,
        success: false,
        error: "Tool execution result missing from runtime.",
      });
    } else {
      toolResults.push(executedResult);
      pending.referenceImages = executed.referenceImagesByResultIndex?.[executedIndex];
    }
    executedIndex += 1;
  }

  callbacks.onToolResult?.(toolResults);
  if (callbacks.onAgentEvent) {
    for (let resultIndex = 0; resultIndex < pendingCalls.length; resultIndex += 1) {
      const pendingCall = pendingCalls[resultIndex];
      const toolResult = toolResults[resultIndex];
      if (!pendingCall || !toolResult) continue;
      callbacks.onAgentEvent({
        kind: "tool_result",
        hop: loopIndex ?? 0,
        callId: pendingCall.callId,
        tool: toolResult.tool,
        ok: toolResult.success,
        result: toolResult,
      });
    }
  }

  const outcomes: NativeToolOutcome[] = pendingCalls.map((pending, index) => ({
    callId: pending.callId,
    tool: pending.tool,
    output: normalizeToolResultForOutput(toolResults[index]),
    isError: toolResults[index]?.success !== true,
    referenceImages: pending.referenceImages,
  }));

  for (let index = 0; index < pendingCalls.length; index += 1) {
    const pending = pendingCalls[index];
    const toolResult = toolResults[index];
    const traceEntry = goldenToolTraceEntries[pending.sequence];
    if (traceEntry) {
      traceEntry.status = resolveTraceStatus({ pending, toolResult });
      traceEntry.success = toolResult?.success;
      traceEntry.error = toolResult?.error;
      traceEntry.files = buildToolTraceFiles(pending.tool);
      traceEntry.requestedReturn = buildRequestedReturn(pending.parsedToolCall);
      traceEntry.referenceImageCount = pending.referenceImages?.length ?? 0;
      traceEntry.hasSlidesInfo = hasSlidesInfo(toolResult);
      traceEntry.createdSlideCount = getCreatedSlideCount(toolResult);
    }
    await captureGoldenToolOutput({
      ctx: agentToolContext,
      subdirectory: pending.goldenSubdirectory,
      sequence: pending.sequence,
      loopIndex,
      callIndexInLoop: pending.callIndexInLoop,
      callId: pending.callId,
      toolName: pending.tool,
      toolResult,
      normalizedFunctionOutput: outcomes[index].output,
      providerResultItem: toProviderResultItem(outcomes[index]),
      referenceImages: pending.referenceImages,
    });
  }

  return {
    outcomes,
    toolResults,
    referenceImages: executed.referenceImages,
  };
}
